import type { VoidFunctionComponent } from 'react';

import { Category } from '../../../shared/issuesV2/common';
import { useIssueCountsPreview } from '../../hooks/api/teams/useIssueCountsPreview';
import Badge from '../generic/Badge/Badge';
import Card from '../generic/Card/Card';
import Loader from '../generic/Loader';

import css from './GlobalIssueCSVExportCard.module.less';

type Props = {
  teamIds: number[];
  releaseGroupId: number | null;
  enabled: boolean;
};

/**
 * Metrics panel for the Global Issue CSV Export card: current active-issue counts per category from the live
 * GET /api/v2/issues/categories, scoped to the selected release group when one is chosen, otherwise the
 * selected teams. It reflects the selected scope, NOT the download include-flags. The static "Daily counts 30
 * days" chip is shown only outside release-group scope (daily counts are not derivable for a release group).
 */
export const GlobalIssueCSVExportMetricsPreview: VoidFunctionComponent<Props> = ({
  teamIds,
  releaseGroupId,
  enabled,
}) => {
  const { data, isError, isLoading } = useIssueCountsPreview(teamIds, releaseGroupId, enabled);

  const renderContent = () => {
    if (isLoading) {
      return <Loader />;
    }

    if (isError || !data) {
      return <span className={css.previewEmpty}>Report metrics unavailable.</span>;
    }

    const licensing = data[Category.Licensing] ?? 0;
    const security = data[Category.Vulnerability] ?? 0;
    const quality = data[Category.Quality] ?? 0;

    return (
      <>
        <span className={css.previewHeader}>Current metrics</span>
        <div className={css.previewChips}>
          <Badge color="white" shape="circle">Licensing {licensing.toLocaleString()}</Badge>
          <Badge color="white" shape="circle">Security {security.toLocaleString()}</Badge>
          <Badge color="white" shape="circle">Quality {quality.toLocaleString()}</Badge>
          {/* Daily counts over time are team/org-only (not derivable for a release group). */}
          {releaseGroupId == null && <Badge color="white" shape="circle">Daily counts 30 days</Badge>}
        </div>
      </>
    );
  };

  return (
    <Card variant="outline" color="dark-gray" padding="none" isFullWidth>
      <Card.Content>
        <div className={css.preview}>{renderContent()}</div>
      </Card.Content>
    </Card>
  );
};
